import React, { useEffect, useMemo, useRef, useState } from 'react'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Drawer from '@mui/material/Drawer'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Tooltip from '@mui/material/Tooltip'
import {
  Broom,
  CaretDown,
  DownloadSimple,
  Funnel,
  MagnifyingGlass,
  SlidersHorizontal,
  X
} from '@phosphor-icons/react'
import { buildSelectValue, getSelectValues } from '../filterSelectState'
import AppChip from '../../chips/AppChip'
import LoadingSpinner from '../../LoadingSpinner'
import {
  cloneFilterSelection,
  hasReportSelection,
  toggleReportSelection
} from '../compactReportSelection'
import FilterUbiquaOptionList from './FilterUbiquaOptionList'
import FilterUbiquaGroupedOptionList from './FilterUbiquaGroupedOptionList'
import {
  FILTER_UBIQUA_INLINE_COUNT,
  getVisibleInlineFilterCount,
  shouldCompactFilterToolbar
} from './filterUbiquaToolbarLayout'

const cloneSelections = (selections = {}) =>
  Object.fromEntries(
    Object.entries(selections).map(([key, value]) => [key, cloneFilterSelection(value)])
  )

const prettyLabel = (title = '') => {
  const normalized = title.trim().toLowerCase()
  if (!normalized) return title
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

const QUICK_FILTER_POPPER_MODIFIERS = [
  { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
  { name: 'preventOverflow', options: { altAxis: true, tether: false } }
]

/**
 * Barra global de búsqueda y filtros para listados de escritorio.
 *
 * Datos que recibe:
 * - `filters`: arreglo de grupos de filtro. Cada grupo requiere
 *   `{ key, label, options }`, donde `options` contiene `{ value, label }`.
 *   También admite `icon`, `inline`, `variant`, `disabled`, `searchPlaceholder`, `loading`,
 *   `hasMore`, `onSearchChange(query)`, `searchOnEnter` y `onLoadMore()`.
 *   `optionGroups: [{ key, label }]` habilita el selector de tipo de opciones;
 *   `activeOptionGroup` y `onOptionGroupChange(key)` permiten consultas remotas por tipo.
 *   `singleSelect` permite una opción; `alwaysInline` conserva el control en móvil.
 *   `showSearch: false` oculta la búsqueda propia en menús cortos.
 *
 * Selección y aplicación:
 * - Por defecto admite múltiples opciones: guarda un borrador hasta pulsar "Aplicar".
 * - `autoApply={true}` guarda cada selección con `onFilterChange` y oculta "Aplicar".
 * - Con `singleSelect`, elegir una opción en el filtro rápido aplica y cierra de inmediato;
 *   en el panel lateral conserva el borrador hasta aplicar el conjunto de filtros.
 * - Con `optionGroups`, los radios cambian el tipo visible (p. ej. marca o producto).
 *   Cada valor usa `${groupKey}::${id}`; cambiar de tipo conserva lo seleccionado en otros grupos.
 * - `globalSelection` permite seleccionar todo el conjunto remoto y guardar sólo exclusiones,
 *   evitando cargar todas las páginas para representar "Seleccionar todos del período".
 *
 * Presentación:
 *   `required` muestra la marca de campo obligatorio. `showInSummary: false`
 *   omite controles siempre visibles del resumen y del contador de filtros.
 * - `appliedFilterSelections`: objeto con las selecciones aplicadas, indexado
 *   por la clave de cada filtro; por ejemplo `{ status: ['pending'] }`.
 * - `searchInput`: texto actual del buscador principal.
 * - `appliedSearchInput`: búsqueda aplicada que muestra el resumen; por defecto usa `searchInput`.
 * - `onFilterChange(key, values)`: actualiza la selección de un grupo.
 * - `onApplyFilters()`, `onResetFilters()`: aplican o limpian los filtros.
 * - `onSearchChange(event)`, `onSearchEnter(event)` y `onSearchClear()`:
 *   manejan la búsqueda principal.
 * - `showSearch` y `showExport` (por defecto `true`): controlan la visibilidad
 *   del buscador principal y del botón CSV; la búsqueda de opciones se conserva.
 * - `onExport()` (opcional): habilita la acción de exportar; `exportDisabled`
 *   y `exportTooltip` controlan su estado y texto.
 * - `title`, `searchPlaceholder`, `disabled`, `compactSearch` y
 *   `defaultInlineFilterCount`: personalizan la presentación.
 * - `title={null}` oculta el encabezado cuando la barra se integra en otra sección.
 * - `headingTrailingContent` coloca información adicional junto al título.
 * - "Más filtros" aparece sólo con más de tres filtros; `supplementaryFilterCount`
 *   incluye fechas u otros controles adicionales en ese total (no la búsqueda).
 * - `showMoreFilters={false}` oculta "Más filtros" y muestra todos los filtros en la barra.
 * - `showResetFilters={false}` oculta el restablecimiento de la barra de filtros aplicados.
 * - `controlsBeforeFirstFilterContent` inserta controles propios antes del primer filtro.
 * - `controlsAfterFirstFilterContent` inserta controles propios después del primer filtro.
 * - `controlsBeforeMoreContent` inserta controles propios antes de "Más filtros".
 * - `drawerExtraContent`, `searchTrailingAction` y `controlsTrailingAction`:
 *   permiten inyectar JSX adicional sin modificar el componente.
 *
 * La pantalla que lo usa conserva la consulta de datos y el estado de filtros;
 * este componente sólo presenta los controles y notifica los cambios.
 */
export default function FilterUbiquaMui({
  filters = [],
  appliedFilterSelections = {},
  isAnyFilterSelected = false,
  searchInput = '',
  appliedSearchInput = searchInput,
  showSearch = true,
  showExport = true,
  showMoreFilters = true,
  showResetFilters = true,
  showFilterTooltips = false,
  autoApply = false,
  disabled = false,
  title = 'Filtrar',
  headingTrailingContent = null,
  searchPlaceholder = 'Buscar…',
  defaultInlineFilterCount = FILTER_UBIQUA_INLINE_COUNT,
  drawerPreviewEmptyLabel = 'Sin filtros seleccionados',
  focusSearchEventName = 'filter-ubiqua-mui:focus-search',
  onFilterChange,
  onApplyFilters,
  onResetFilters,
  onSearchChange,
  onSearchEnter,
  onSearchClear,
  drawerExtraContent = null,
  searchTrailingAction = null,
  controlsTrailingAction = null,
  controlsBeforeFirstFilterContent = null,
  controlsAfterFirstFilterContent = null,
  controlsBeforeMoreContent = null,
  onExport,
  exportDisabled = false,
  exportTooltip = 'Descargar tabla en CSV',
  exportAriaLabel = 'Descargar tabla en CSV',
  compactSearch = false,
  supplementaryAppliedGroups = [],
  supplementaryFilterCount = 0,
  supplementaryActiveCount = 0,
  supplementaryDraftCount = 0,
  onDrawerOpen,
  onDrawerReset
}) {
  const searchRef = useRef(null)
  const rootRef = useRef(null)
  const optionSearchQueriesRef = useRef({})
  const [toolbarWidth, setToolbarWidth] = useState(Infinity)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [quickAnchor, setQuickAnchor] = useState(null)
  const [quickKey, setQuickKey] = useState(null)
  const [draftSelections, setDraftSelections] = useState(() =>
    cloneSelections(appliedFilterSelections)
  )
  const currentSelections = autoApply ? appliedFilterSelections : draftSelections
  const hasMoreFilters = showMoreFilters && filters.length + supplementaryFilterCount > 3
  const compactToolbar = shouldCompactFilterToolbar(toolbarWidth)
  const visibleInlineCount = getVisibleInlineFilterCount(toolbarWidth, defaultInlineFilterCount)

  const inlineFilters = useMemo(() => {
    if (!hasMoreFilters) return filters
    const eligible = filters.filter((filter) => filter.inline !== false)
    const pinned = eligible.filter((filter) => filter.alwaysInline)
    const available = Math.max(
      0,
      Math.min(defaultInlineFilterCount, visibleInlineCount) - pinned.length
    )
    const additional = eligible.filter((filter) => !filter.alwaysInline).slice(0, available)
    return eligible.filter((filter) => filter.alwaysInline || additional.includes(filter))
  }, [defaultInlineFilterCount, filters, visibleInlineCount, hasMoreFilters])

  const drawerFilters = filters

  const resolveGroupLabel = (filter) => prettyLabel(filter.label || filter.title || filter.key)

  const activeFilterCount =
    filters.filter(
      (filter) =>
        filter.showInSummary !== false && hasReportSelection(appliedFilterSelections[filter.key])
    ).length + supplementaryActiveCount

  useEffect(() => {
    if (!focusSearchEventName) return undefined
    const focusSearch = () => searchRef.current?.focus()
    window.addEventListener(focusSearchEventName, focusSearch)
    return () => window.removeEventListener(focusSearchEventName, focusSearch)
  }, [focusSearchEventName])

  useEffect(() => {
    const node = rootRef.current
    if (!node || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(([entry]) => {
      setToolbarWidth(entry.contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const updateSelection = (key, selection) => {
    setDraftSelections((current) => ({ ...current, [key]: selection }))
    if (autoApply) onFilterChange(key, cloneFilterSelection(selection))
  }

  const setDraftValues = (key, values) => updateSelection(key, buildSelectValue(values))

  const toggleDraftValue = (key, value) => {
    if (filters.find((filter) => filter.key === key)?.singleSelect) {
      setDraftValues(key, [value])
      if (!drawerOpen) {
        if (!autoApply) onFilterChange(key, buildSelectValue([value]))
        closeQuickFilter()
      }
      return
    }
    if (filters.find((filter) => filter.key === key)?.globalSelection) {
      updateSelection(key, toggleReportSelection(currentSelections[key], value))
      return
    }
    const currentValues = getSelectValues(currentSelections[key])
    setDraftValues(
      key,
      currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value]
    )
  }

  const selectAllVisible = (key, visibleValues, checked) => {
    if (filters.find((filter) => filter.key === key)?.globalSelection) {
      updateSelection(
        key,
        checked ? { mode: 'all', values: [], explicit: true } : buildSelectValue()
      )
      return
    }
    const currentValues = new Set(getSelectValues(currentSelections[key]))
    visibleValues.forEach((value) => {
      if (checked) currentValues.add(value)
      else currentValues.delete(value)
    })
    setDraftValues(key, [...currentValues])
  }

  const openQuickFilter = (event, key) => {
    setDraftSelections(cloneSelections(appliedFilterSelections))
    setQuickKey(key)
    setQuickAnchor(event.currentTarget)
  }

  const closeQuickFilter = () => {
    setQuickAnchor(null)
    setQuickKey(null)
  }

  useEffect(() => {
    if (!quickAnchor) return undefined
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return
      closeQuickFilter()
      quickAnchor.focus()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [quickAnchor])

  const openDrawer = () => {
    setDraftSelections(cloneSelections(appliedFilterSelections))
    onDrawerOpen?.()
    setDrawerOpen(true)
  }

  const closeDrawer = () => setDrawerOpen(false)

  const commitDraft = (selections = draftSelections) => {
    if (onApplyFilters) return onApplyFilters(cloneSelections(selections))
    else Object.entries(selections).forEach(([key, value]) => onFilterChange(key, value))
  }

  const applyQuickFilter = () => {
    if (!quickKey) return
    onFilterChange(quickKey, cloneFilterSelection(draftSelections[quickKey]))
    closeQuickFilter()
  }

  const applyDrawerFilters = () => {
    if (commitDraft() === false) return
    closeDrawer()
  }

  const resetDraft = () => {
    if (autoApply) {
      onResetFilters?.()
      return
    }
    setDraftSelections(
      Object.fromEntries(filters.map((filter) => [filter.key, buildSelectValue()]))
    )
    onDrawerReset?.()
  }

  const removeGroup = (key) => {
    if (key === 'query') {
      onSearchClear?.()
      return
    }
    const supplementaryGroup = supplementaryAppliedGroups.find((group) => group.key === key)
    if (supplementaryGroup) {
      supplementaryGroup.onRemove?.()
      return
    }
    onFilterChange(key, buildSelectValue())
  }

  const triggerLabel = (filter) => {
    if (filter.singleSelect) {
      const selected = getSelectValues(appliedFilterSelections[filter.key])[0]
      return (
        filter.options.find((option) => option.value === selected)?.label ||
        resolveGroupLabel(filter)
      )
    }
    return resolveGroupLabel(filter)
  }

  const displayedCount = (filter) => getSelectValues(appliedFilterSelections[filter.key]).length

  const draftCount = useMemo(
    () =>
      filters.reduce(
        (count, filter) =>
          count +
          Number(
            filter.showInSummary !== false && hasReportSelection(currentSelections[filter.key])
          ),
        0
      ) + supplementaryDraftCount,
    [currentSelections, filters, supplementaryDraftCount]
  )

  const groupedAppliedFilters = useMemo(() => {
    const groups = []
    if (appliedSearchInput.trim()) {
      groups.push({ key: 'query', label: 'Búsqueda', values: [appliedSearchInput.trim()] })
    }
    filters.forEach((filter) => {
      if (filter.showInSummary === false) return
      const selection = appliedFilterSelections[filter.key]
      if (filter.globalSelection && (selection?.mode === 'exclude' || selection?.explicit)) {
        groups.push({
          key: filter.key,
          label: resolveGroupLabel(filter),
          values: [
            {
              value: 'all',
              label:
                selection.mode === 'exclude' ? `Todos excepto ${selection.values.length}` : 'Todos'
            }
          ]
        })
        return
      }
      const values = getSelectValues(selection)
      if (!values.length) return
      groups.push({
        key: filter.key,
        label: resolveGroupLabel(filter),
        values: values.map((value) => ({
          value,
          label: filter.options.find((option) => option.value === value)?.label || value
        }))
      })
    })
    supplementaryAppliedGroups.forEach((group) => {
      if (!group?.values?.length) return
      groups.push(group)
    })
    return groups
  }, [appliedFilterSelections, filters, appliedSearchInput, supplementaryAppliedGroups])

  const visibleGroups = groupedAppliedFilters.slice(0, 2)
  const hiddenGroupCount = Math.max(groupedAppliedFilters.length - 2, 0)

  const renderOptionList = (filter) => {
    const OptionList = filter.optionGroups?.length
      ? FilterUbiquaGroupedOptionList
      : FilterUbiquaOptionList
    return (
      <OptionList
        key={`${filter.key}-${drawerOpen || quickKey || 'idle'}`}
        filterKey={filter.key}
        optionGroups={filter.optionGroups}
        activeOptionGroup={filter.activeOptionGroup}
        onOptionGroupChange={filter.onOptionGroupChange}
        singleSelect={filter.singleSelect}
        disabled={disabled || filter.disabled}
        options={filter.options}
        selectedValues={getSelectValues(currentSelections[filter.key])}
        globalSelection={filter.globalSelection}
        selectAllLabel={filter.selectAllLabel}
        selection={currentSelections[filter.key]}
        searchPlaceholder={filter.searchPlaceholder || 'Buscar ...'}
        showSearch={filter.showSearch !== false}
        autoFocus={quickKey === filter.key && !drawerOpen}
        onToggle={toggleDraftValue}
        onClear={(key, values) => {
          setDraftValues(key, values)
          if (filter.singleSelect && !drawerOpen) {
            if (!autoApply) onFilterChange(key, buildSelectValue(values))
            closeQuickFilter()
          }
        }}
        onSelectAllVisible={selectAllVisible}
        initialSearchQuery={optionSearchQueriesRef.current[filter.key] || ''}
        onSearchChange={
          typeof filter.onSearchChange === 'function'
            ? (query) => {
                optionSearchQueriesRef.current[filter.key] = query
                filter.onSearchChange(query)
              }
            : undefined
        }
        remoteSearchQuery={filter.remoteSearchQuery}
        searchOnEnter={filter.searchOnEnter}
        onLoadMore={filter.onLoadMore}
        hasMore={filter.hasMore}
        loading={filter.loading}
        optionsDisabled={filter.loadingInTrigger && filter.loading}
        showLoadingIndicator={!filter.loadingInTrigger}
        error={filter.error}
        onRetry={filter.onRetry}
      />
    )
  }

  const quickFilter = filters.find((filter) => filter.key === quickKey)

  return (
    <section
      ref={rootRef}
      className={`filter-ubiqua-mui-filters${!showSearch ? ' filter-ubiqua-mui-filters--without-search' : ''}${!hasMoreFilters ? ' filter-ubiqua-mui-filters--all-inline' : ''}${compactSearch ? ' filter-ubiqua-mui-filters--compact-search' : ''}${compactToolbar ? ' filter-ubiqua-mui-filters--compact-toolbar' : ''}`}
      aria-labelledby={title ? 'filter-ubiqua-mui-filters-title' : undefined}
      aria-label={title ? undefined : 'Filtros'}
    >
      {title ? (
        <div className="filter-ubiqua-mui-filter-heading">
          <div className="filter-ubiqua-mui-filter-title">
            <SlidersHorizontal size={17} weight="regular" aria-hidden="true" />
            <h2 id="filter-ubiqua-mui-filters-title">{title}</h2>
          </div>
          {headingTrailingContent}
        </div>
      ) : null}

      <div className="filter-ubiqua-mui-filter-main">
        {showSearch ? (
          <div className="filter-ubiqua-mui-local-search">
            <div className="filter-ubiqua-mui-search-row">
              <div className="filter-ubiqua-mui-search-field">
                <MagnifyingGlass size={17} weight="regular" aria-hidden="true" />
                <input
                  id="filter-ubiqua-mui-tower-search"
                  aria-label="Buscar en este listado"
                  ref={searchRef}
                  type="search"
                  value={searchInput}
                  disabled={disabled}
                  onChange={onSearchChange}
                  onKeyDown={onSearchEnter}
                  placeholder={searchPlaceholder}
                  autoComplete="off"
                />
                {searchInput ? (
                  <button
                    type="button"
                    className="filter-ubiqua-mui-icon-btn"
                    onClick={onSearchClear}
                    aria-label="Limpiar búsqueda"
                  >
                    <X size={13} />
                  </button>
                ) : null}
              </div>
              {searchTrailingAction ? (
                <div className="filter-ubiqua-mui-search-trailing">{searchTrailingAction}</div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="filter-ubiqua-mui-filter-controls">
          {controlsBeforeFirstFilterContent}
          {inlineFilters.map((filter, index) => {
            const Icon = filter.icon || Funnel
            const loadingTrigger = Boolean(filter.loadingInTrigger && filter.loading)
            const selection = appliedFilterSelections[filter.key]
            const selectedCount =
              filter.globalSelection && hasReportSelection(selection)
                ? Math.max(1, selection.values?.length || 0)
                : getSelectValues(selection).length
            const count = displayedCount(filter)
            const filterTooltip =
              filter.disabledReason ||
              getSelectValues(appliedFilterSelections[filter.key])
                .map(
                  (value) => filter.options.find((option) => option.value === value)?.label || value
                )
                .join(', ') ||
              triggerLabel(filter)
            return (
              <React.Fragment key={filter.key}>
                <Tooltip
                  title={showFilterTooltips && quickKey !== filter.key ? filterTooltip : ''}
                  placement="top"
                  arrow
                  describeChild
                >
                  <div
                    className={`filter-ubiqua-mui-filter-control${filter.variant ? ` is-${filter.variant}` : ''}${loadingTrigger ? ' is-loading' : ''}`}
                  >
                    <button
                      type="button"
                      className={`filter-ubiqua-mui-filter-trigger ${selectedCount ? 'has-value' : ''} ${quickKey === filter.key ? 'is-open' : ''}`}
                      disabled={disabled || filter.disabled || loadingTrigger}
                      aria-busy={loadingTrigger || undefined}
                      aria-labelledby={`filter-ubiqua-mui-filter-value-${filter.key}`}
                      aria-haspopup="dialog"
                      aria-expanded={quickKey === filter.key}
                      aria-controls={
                        quickKey === filter.key ? 'filter-ubiqua-mui-quick-filter' : undefined
                      }
                      title={showFilterTooltips ? undefined : filterTooltip}
                      onClick={(event) => openQuickFilter(event, filter.key)}
                    >
                      <Icon size={16} weight="regular" aria-hidden="true" />
                      <span
                        className="filter-ubiqua-mui-filter-value"
                        id={`filter-ubiqua-mui-filter-value-${filter.key}`}
                      >
                        {loadingTrigger
                          ? `${filter.loadingLabel || resolveGroupLabel(filter)}…`
                          : triggerLabel(filter)}
                        {filter.required && (!filter.singleSelect || !selectedCount) ? (
                          <span className="filter-ubiqua-mui-required"> *</span>
                        ) : null}
                      </span>
                      {!loadingTrigger &&
                      (count || (filter.globalSelection && hasReportSelection(selection))) &&
                      !filter.singleSelect ? (
                        <span className="filter-ubiqua-mui-filter-count">
                          {filter.globalSelection && selection?.mode === 'exclude'
                            ? `Excepto ${selection.values.length}`
                            : filter.globalSelection && selection?.explicit
                              ? 'Todos'
                              : `+${count}`}
                        </span>
                      ) : null}
                      {loadingTrigger ? (
                        <span className="filter-ubiqua-mui-filter-spinner" aria-hidden="true">
                          <LoadingSpinner text="" size={16} />
                        </span>
                      ) : (
                        <CaretDown
                          className="filter-ubiqua-mui-filter-chevron"
                          size={14}
                          weight="bold"
                        />
                      )}
                    </button>
                  </div>
                </Tooltip>
                {index === 0 && controlsAfterFirstFilterContent}
              </React.Fragment>
            )
          })}
          {controlsBeforeMoreContent}
          {hasMoreFilters && (
            <button
              type="button"
              className={`filter-ubiqua-mui-more-filter ${drawerOpen ? 'is-open' : ''}`}
              disabled={disabled}
              aria-label={
                activeFilterCount
                  ? `Más filtros, ${activeFilterCount} ${activeFilterCount === 1 ? 'tipo de filtro aplicado' : 'tipos de filtro aplicados'}`
                  : 'Más filtros'
              }
              aria-haspopup="dialog"
              aria-expanded={drawerOpen}
              aria-controls="filter-ubiqua-mui-filter-drawer"
              title="Más filtros"
              onClick={() => (drawerOpen ? closeDrawer() : openDrawer())}
            >
              <SlidersHorizontal size={19} weight="regular" aria-hidden="true" />
              <span>Más filtros</span>
              {activeFilterCount ? (
                <span className="filter-ubiqua-mui-more-count" key={activeFilterCount}>
                  +{activeFilterCount}
                </span>
              ) : null}
            </button>
          )}
          {controlsTrailingAction ? (
            <div className="filter-ubiqua-mui-controls-trailing">{controlsTrailingAction}</div>
          ) : null}
          {showExport ? (
            <div className="filter-ubiqua-mui-controls-trailing">
              <Tooltip title={exportTooltip} placement="top" arrow>
                <span>
                  <button
                    type="button"
                    className="filter-ubiqua-mui-toolbar-icon-btn"
                    onClick={onExport}
                    disabled={disabled || exportDisabled || !onExport}
                    aria-label={exportAriaLabel}
                  >
                    <DownloadSimple size={19} weight="regular" aria-hidden="true" />
                  </button>
                </span>
              </Tooltip>
            </div>
          ) : null}
        </div>
      </div>

      {groupedAppliedFilters.length ? (
        <div className="filter-ubiqua-mui-active-bar" aria-live="polite">
          <span className="filter-ubiqua-mui-active-label">Aplicados:</span>
          <div className="filter-ubiqua-mui-active-chips">
            {visibleGroups.map((group) => {
              const firstLabel = group.key === 'query' ? group.values[0] : group.values[0].label
              const label = `${group.label}: ${firstLabel}`
              return (
                <AppChip
                  key={group.key}
                  label={
                    <>
                      <span className="filter-ubiqua-mui-chip-copy">{label}</span>
                      {group.key !== 'query' ? (
                        <span className="filter-ubiqua-mui-filter-count">
                          +{group.values.length}
                        </span>
                      ) : null}
                    </>
                  }
                  onDelete={() => removeGroup(group.key)}
                  ariaLabel={`Quitar filtro ${group.label}`}
                  variant="filter-ubiqua"
                />
              )
            })}
            {hiddenGroupCount ? (
              <span className="filter-ubiqua-mui-hidden-count">+{hiddenGroupCount}</span>
            ) : null}
          </div>
          {showResetFilters && (isAnyFilterSelected || appliedSearchInput.trim()) ? (
            <button
              type="button"
              className="filter-ubiqua-mui-text-btn filter-ubiqua-mui-reset-btn"
              disabled={disabled}
              onClick={onResetFilters}
            >
              <Broom size={15} weight="regular" aria-hidden="true" />
              Restablecer filtros aplicados
            </button>
          ) : null}
        </div>
      ) : null}

      <Popper
        open={Boolean(quickAnchor)}
        anchorEl={quickAnchor}
        placement="bottom-start"
        modifiers={QUICK_FILTER_POPPER_MODIFIERS}
        className="filter-ubiqua-mui-quick-popper"
      >
        {quickFilter ? (
          <ClickAwayListener onClickAway={closeQuickFilter}>
            <Paper
              id="filter-ubiqua-mui-quick-filter"
              className="filter-ubiqua-mui-quick-popover"
              role="dialog"
              aria-label={`Filtro ${resolveGroupLabel(quickFilter)}`}
              onBlur={(event) => {
                // Al pulsar el texto de un <label>, el buscador pierde el foco con
                // relatedTarget=null antes de que el navegador active su checkbox.
                // ClickAwayListener ya se encarga de cerrar ante clics externos.
                if (
                  event.relatedTarget &&
                  !event.currentTarget.contains(event.relatedTarget) &&
                  event.relatedTarget !== quickAnchor
                ) {
                  closeQuickFilter()
                }
              }}
            >
              {renderOptionList(quickFilter)}
              {!quickFilter.singleSelect ? (
                <div className="filter-ubiqua-mui-picker-footer">
                  <span className="filter-ubiqua-mui-foot-note">
                    {quickFilter.globalSelection &&
                    (currentSelections[quickFilter.key]?.mode === 'exclude' ||
                      currentSelections[quickFilter.key]?.explicit)
                      ? currentSelections[quickFilter.key].mode === 'exclude'
                        ? `Todos excepto ${currentSelections[quickFilter.key].values.length}`
                        : 'Todos seleccionados'
                      : getSelectValues(currentSelections[quickFilter.key]).length
                        ? `${getSelectValues(currentSelections[quickFilter.key]).length} seleccionado${
                            getSelectValues(currentSelections[quickFilter.key]).length === 1
                              ? ''
                              : 's'
                          }`
                        : 'Sin filtro en esta categoría'}
                  </span>
                  {!autoApply && (
                    <button
                      type="button"
                      className="filter-ubiqua-mui-btn filter-ubiqua-mui-btn--blue"
                      onClick={applyQuickFilter}
                      disabled={Boolean(quickFilter.loadingInTrigger && quickFilter.loading)}
                    >
                      Aplicar
                    </button>
                  )}
                </div>
              ) : null}
            </Paper>
          </ClickAwayListener>
        ) : null}
      </Popper>

      <Drawer
        anchor="right"
        variant="temporary"
        className="filter-ubiqua-mui-filter-drawer"
        open={drawerOpen}
        onClose={closeDrawer}
        transitionDuration={{ enter: 380, exit: 180 }}
        PaperProps={{
          id: 'filter-ubiqua-mui-filter-drawer',
          className: 'filter-ubiqua-mui-filter-panel',
          'aria-labelledby': 'filter-ubiqua-mui-drawer-title'
        }}
      >
        <aside aria-label="Todos los filtros">
          <header className="filter-ubiqua-mui-drawer-head">
            <div className="filter-ubiqua-mui-drawer-heading">
              <span className="filter-ubiqua-mui-drawer-icon" aria-hidden="true">
                <SlidersHorizontal size={20} />
              </span>
              <h2 id="filter-ubiqua-mui-drawer-title">Todos los filtros</h2>
            </div>
            <button
              type="button"
              className="filter-ubiqua-mui-icon-btn"
              onClick={closeDrawer}
              aria-label="Cerrar filtros"
            >
              <X size={16} />
            </button>
          </header>
          <div className="filter-ubiqua-mui-drawer-body">
            {drawerFilters.map((filter) => {
              const Icon = filter.icon || Funnel
              const selected = getSelectValues(currentSelections[filter.key]).length
              return (
                <details key={filter.key} className="filter-ubiqua-mui-filter-accordion">
                  <summary>
                    <Icon size={17} weight="regular" aria-hidden="true" />
                    <strong>{resolveGroupLabel(filter)}</strong>
                    {selected ? (
                      <span
                        className="filter-ubiqua-mui-filter-count"
                        aria-label={`${selected} opciones seleccionadas`}
                        key={selected}
                      >
                        +{selected}
                      </span>
                    ) : null}
                    <CaretDown className="filter-ubiqua-mui-filter-chevron" size={14} />
                  </summary>
                  <div className="filter-ubiqua-mui-accordion-content">
                    {renderOptionList(filter)}
                  </div>
                </details>
              )
            })}
            {drawerExtraContent ? (
              <div className="filter-ubiqua-mui-drawer-extra">{drawerExtraContent}</div>
            ) : null}
          </div>
          <footer className="filter-ubiqua-mui-drawer-footer">
            <div className="filter-ubiqua-mui-drawer-preview">
              <span>{autoApply ? 'Selección actual' : 'Selección al aplicar'}</span>
              <strong>
                {draftCount
                  ? `${draftCount} ${draftCount === 1 ? 'filtro seleccionado' : 'filtros seleccionados'}`
                  : drawerPreviewEmptyLabel}
              </strong>
            </div>
            <div className="filter-ubiqua-mui-drawer-footer-buttons">
              <button
                type="button"
                className="filter-ubiqua-mui-btn"
                onClick={resetDraft}
                disabled={draftCount === 0}
              >
                Restablecer
              </button>
              {!autoApply && (
                <button
                  type="button"
                  className="filter-ubiqua-mui-btn filter-ubiqua-mui-btn--primary"
                  onClick={applyDrawerFilters}
                >
                  Aplicar
                </button>
              )}
            </div>
          </footer>
        </aside>
      </Drawer>
    </section>
  )
}
